// AssinarPlano.js
import React, { useEffect, useRef, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Chip,
  Divider,
  CircularProgress,
  InputAdornment,
  Stack,
  useMediaQuery,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Link
} from "@mui/material";

import { useTheme, alpha } from "@mui/material/styles";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";

import DiscountIcon from '@mui/icons-material/Discount';

import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import PixIcon from "@mui/icons-material/Pix";

import CloseIcon from "@mui/icons-material/Close";

import TermoUsoConteudo from "../components/Legal/TermoUso";
import PixSubscriptionDialog from "../components/Billing/PixSubscriptionDialog";

import api from "../services/api";
import { getApiErrorMessage, normalizeTeacher } from "../services/apiUtils";

import { useSnackbar } from "../contexts/SnackbarContext";
import { useAuth } from "../contexts/AuthContext";

// ===== helpers =====
function isNomeValido(nome) {
  return /^[A-Za-zÀ-ÿ\s]+$/.test(nome);
}

function onlyLettersSpaces(value = "") {
  return String(value)
    .replace(/[^A-Za-zÀ-ÖØ-öø-ÿ\s]/g, "")
    .replace(/\s{2,}/g, " ")
    .trimStart();
}

function toTitleCaseNome(value = "") {
  const cleaned = onlyLettersSpaces(value).trim();
  if (!cleaned) return "";

  return cleaned
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function mascaraCpfCnpj(value = "") {
  value = value.replace(/\D/g, "");
  return value
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2")
    .slice(0, 14);
}

function mascaraCEP(value = "") {
  return value
    .replace(/\D/g, "")
    .slice(0, 8)
    .replace(/(\d{5})(\d)/, "$1-$2");
}

function mascaraCelular(value = "") {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 10) {
    return digits
      .replace(/^(\d{0,2})/, "($1")
      .replace(/^\((\d{2})(\d)/, "($1) $2")
      .replace(/(\d{4})(\d)/, "$1-$2");
  }
  return digits
    .replace(/^(\d{0,2})/, "($1")
    .replace(/^\((\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2");
}

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const INITIAL_FORM = {
  nome: "",
  email: "",
  cpf_cnpj: "",
  senha: "",
  confirmar_senha: "",
  cep: "",
  endereco: "",
  numero: "",
  complemento: "",
  bairro: "",
  cidade: "",
  uf: "",
  celular: "",
  referralCode: "",
};

const PLANOS_VALIDOS = new Set(["PROFESSOR", "MENTOR", "MESTRE"]);

// ===== Cards IGUAIS ao Header.js =====
const PLANS = [
  {
    tipo: "PROFESSOR",
    title: "Professor",
    priceDisabled: "R$39,90",
    price: "R$29,90/mês",
    infoText: "* Ideal para professores com baixa demanda de criação.",
    features: [
      "10 documentos com IA por mês",
      "Alinhamento total à BNCC",
      "Edição fácil antes de exportar",
      "Exportação para Word e PDF",
      "Exportação do gabarito",
      "Suporte por WhatsApp",
    ],
    highlight: false,
  },
  {
    tipo: "MENTOR",
    title: "Mentor",
    priceDisabled: "R$59,90",
    price: "R$49,90/mês",
    infoText: "* Para professores que produzem conteúdos com frequência.",
    features: [
      "25 documentos com IA por mês",
      "Alinhamento total à BNCC",
      "Edição fácil antes de exportar",
      "Exportação para Word e PDF",
      "Exportação do gabarito",
      "10 imagens com IA por mês",
      "Suporte por WhatsApp",
    ],
    highlight: true,
  },
  {
    tipo: "MESTRE",
    title: "Mestre",
    priceDisabled: "R$69,90",
    price: "R$69,90/mês",
    infoText: "* Pensado para professores com alta demanda de criação.",
    features: [
      "50 documentos com IA por mês",
      "Alinhamento total à BNCC",
      "Edição fácil antes de exportar",
      "Exportação para Word e PDF",
      "Exportação do gabarito",
      "20 imagens com IA por mês",
      "Suporte por WhatsApp",
    ],
    highlight: false,
  },
];

const PROFESSOR_EXTRAS_RISCADOS = [
  "Geração de imagens com IA",
];

const MENTOR_EXTRAS_RISCADOS = [];

const PLAN_LABEL = {
  PROFESSOR: "Plano Professor",
  MENTOR: "Plano Mentor",
  MESTRE: "Plano Mestre",
};

function parseBRLToNumber(price = "") {
  return Number(
    String(price)
      .replace(/[^\d,]/g, "")
      .replace(".", "")
      .replace(",", ".")
  ) || 0;
}

function formatBRL(value = 0) {
  return Number(value).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function calcDiscountedPrice(valorOriginal, descontoPercentual) {
  const percentual = Math.max(0, Math.min(100, Number(descontoPercentual || 0)));
  const valor = Number(valorOriginal || 0);
  return Math.round((valor * (1 - percentual / 100) + Number.EPSILON) * 100) / 100;
}

function getPlanMonthlyValue(planName) {
  const plan = PLANS.find((item) => item.tipo === planName);
  return parseBRLToNumber(plan?.price);
}

function normalizePixDialogData(raw = {}, planName = null) {
  const source = raw?.pixPayment && typeof raw.pixPayment === "object" ? raw.pixPayment : raw;

  return {
    ...source,
    planName: planName || raw?.planName || raw?.plan || raw?.plano,
    amount: source?.amount || source?.value || source?.valor || getPlanMonthlyValue(planName || raw?.planName || raw?.plan || raw?.plano),
    paymentId: source?.paymentId || source?.payment_id || raw?.paymentId || raw?.payment_id,
    pixPayload: source?.pixPayload || source?.pix_payload || source?.payload || raw?.pixPayload || raw?.pix_payload || raw?.payload,
    pixEncodedImage: source?.pixEncodedImage || source?.pix_encoded_image || source?.encodedImage || raw?.pixEncodedImage || raw?.pix_encoded_image || raw?.encodedImage,
    pixExpirationDate: source?.pixExpirationDate || source?.pix_expiration_date || source?.expirationDate || raw?.pixExpirationDate || raw?.pix_expiration_date || raw?.expirationDate,
    dueDate: source?.dueDate || source?.due_date || raw?.dueDate || raw?.due_date,
    invoiceUrl: source?.invoiceUrl || source?.invoice_url || raw?.invoiceUrl || raw?.invoice_url,
  };
}

export default function AssinarPlano() {
  const { login } = useAuth();
  const { showSnackbar } = useSnackbar();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();

  const CTA_GREEN_PLANOS = "#128C7E";
  const CTA_GREEN_PLANOS_HOVER = "#0E6F64";

  const CTA_GREEN_HIGHLIGHT = "#1BC5B1";
  const CTA_GREEN_HIGHLIGHT_HOVER = "#17B2A2";

  const params = useParams();
  const query = useQuery();

  const planoFromUrlRaw = String(params?.plano || query.get("plano") || "")
    .toUpperCase()
    .trim();

  const [planoSelecionado, setPlanoSelecionado] = useState(
    PLANOS_VALIDOS.has(planoFromUrlRaw) ? planoFromUrlRaw : null,
  );
  const [paymentMethod, setPaymentMethod] = useState("CARD");
  const [pixOpen, setPixOpen] = useState(false);
  const [pixData, setPixData] = useState(null);

  const [form, setForm] = useState(INITIAL_FORM);
  const [loadingCheckout, setLoadingCheckout] = useState(false);

  const passwordValue = form.senha.trim();

  const passwordRules = [
    {
      key: "minLength",
      valid: passwordValue.length >= 6,
      message: "mínimo de 6 caracteres",
    },
    {
      key: "hasLetter",
      valid: /[A-Za-zÀ-ÿ]/.test(passwordValue),
      message: "pelo menos uma letra",
    },
    {
      key: "hasNumber",
      valid: /\d/.test(passwordValue),
      message: "pelo menos um número",
    },
  ];

  const pendingPasswordRules = passwordRules.filter((rule) => !rule.valid);

  const isPasswordValid = pendingPasswordRules.length === 0;

  const passwordHelperText =
    form.senha.length === 0
      ? " "
      : isPasswordValid
        ? "Senha válida."
        : `Ainda falta: ${pendingPasswordRules.map((rule) => rule.message).join(", ")}.`;

  const [termoAceito, setTermoAceito] = useState(false);
  const [openTermoModal, setOpenTermoModal] = useState(false);

  const [indicacaoBloqueada, setIndicacaoBloqueada] = useState(false);

  const [cupomInfo, setCupomInfo] = useState({
    loading: false,
    valid: false,
    descontoPercentual: 0,
    indicacaoRef: "",
  });

  const [emailCheck, setEmailCheck] = useState({
    loading: false,
    exists: false,
    checkedEmail: "",
    helperText: "",
  });


  // Lê o parâmetro de indicação ?ref=... da URL (ex: ?ref=prof_maria_01)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refFromUrl = params.get("indicacao");

    if (refFromUrl) {
      setForm((prev) => ({ ...prev, referralCode: refFromUrl }));
      setIndicacaoBloqueada(true);

      let active = true;

      const fetchCupom = async () => {
        setCupomInfo({
          loading: true,
          valid: false,
          descontoPercentual: 0,
          indicacaoRef: refFromUrl,
        });

        try {
          const res = await api.get(`/partner/cupom/${encodeURIComponent(refFromUrl)}`);

          if (!active) return;

          setCupomInfo({
            loading: false,
            valid: !!res.data?.valid,
            descontoPercentual: Number(res.data?.couponDiscountPercentage || 0),
            indicacaoRef: refFromUrl,
          });
        } catch (e) {
          if (!active) return;

          setCupomInfo({
            loading: false,
            valid: false,
            descontoPercentual: 0,
            indicacaoRef: refFromUrl,
          });
        }
      };

      fetchCupom();

      return () => {
        active = false;
      };
    } else {
      setIndicacaoBloqueada(false);
      setCupomInfo({
        loading: false,
        valid: false,
        descontoPercentual: 0,
        indicacaoRef: "",
      });
    }
  }, [location.search]);

  // Tabs
  const [tab, setTab] = useState(0);

  // Cartão
  const [cardForm, setCardForm] = useState({
    number: "",
    name: "",
    expiry: "",
    cvv: "",
  });
  const digits = (v = "") => String(v).replace(/\D/g, "");

  const CARD_RULES = {
    amex: { max: 15, groups: [4, 6, 5] },
    diners: { max: 14, groups: [4, 6, 4] }, // comum na prática (varia, mas 14 fixo)
    default: { max: 16, groups: [4, 4, 4, 4] }, // visa/master/elo/hipercard/discover/jcb
  };

  const formatCardNumber = (v) => {
    const rawDigits = digits(v);

    // detecta bandeira pelo que já foi digitado (sem depender do state)
    const b = detectBrand(rawDigits);
    const rule = CARD_RULES[b] || CARD_RULES.default;

    // NUNCA deixa passar do tamanho máximo (15/14/16)
    const d = rawDigits.slice(0, rule.max);

    // monta grupos com espaços conforme regra
    const parts = [];
    let i = 0;
    for (const size of rule.groups) {
      if (i >= d.length) break;
      parts.push(d.slice(i, i + size));
      i += size;
    }
    // fallback (se sobrar algo por qualquer motivo)
    if (i < d.length) parts.push(d.slice(i));

    return parts.join(" ");
  };

  const formatExpiry = (v) => {
    const d = digits(v).slice(0, 4);
    if (d.length <= 2) return d;
    return `${d.slice(0, 2)}/${d.slice(2)}`;
  };
  const onlyLetters = (v) =>
    String(v)
      .replace(/[^A-Za-zÀ-ÖØ-öø-ÿ\s]/g, "")
      .replace(/\s{2,}/g, " ");

  const detectBrand = (num) => {
    const n = digits(num);

    // Amex
    if (/^3[47]/.test(n)) return "amex";

    // Visa
    if (/^4/.test(n)) return "visa";

    // Mastercard (prefixos modernos)
    if (/^(5[1-5]|2[2-7])/.test(n)) return "mastercard";

    // Hipercard
    if (/^(606282|3841)/.test(n)) return "hipercard";

    // Elo (principais BINs)
    if (
      /^(4011|4312|4389|4514|4576|5041|5066|5067|5090|6277|6362|6363)/.test(n)
    )
      return "elo";

    // Diners (14 dígitos)
    if (/^(36|30[0-5]|38|39)/.test(n)) return "diners";

    // Discover
    if (/^(6011|65|64[4-9]|622)/.test(n)) return "discover";

    // JCB
    if (/^35/.test(n)) return "jcb";

    return null;
  };

  const brand = detectBrand(cardForm.number);

  // CEP auto-complete
  const [cepStatus, setCepStatus] = useState("idle"); // idle | loading | ok | error
  const [cepHelper, setCepHelper] = useState("");
  const lastFetchedCepRef = useRef("");
  const debounceRef = useRef(null);

  const fetchCep = async (cepDigits) => {
    if (!cepDigits || cepDigits.length !== 8) return;

    setCepStatus("loading");
    setCepHelper("");

    try {
      const res = await fetch(`https://viacep.com.br/ws/${cepDigits}/json/`);
      const data = await res.json();

      if (!data || data.erro) {
        setCepStatus("error");
        setCepHelper("CEP não encontrado.");
        return;
      }

      setForm((prev) => ({
        ...prev,
        endereco: data.logradouro || prev.endereco,
        bairro: data.bairro || prev.bairro,
        cidade: data.localidade || prev.cidade,
        uf: data.uf || prev.uf,
        complemento: prev.complemento || "",
      }));

      setCepStatus("ok");
      setCepHelper("Endereço preenchido automaticamente.");

      document.getElementById("Numero")?.focus();
      lastFetchedCepRef.current = cepDigits;
    } catch (e) {
      setCepStatus("error");
      setCepHelper("Falha ao buscar CEP.");
    }
  };

  useEffect(() => {
    const onlyDigits = (form.cep || "").replace(/\D/g, "");
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (onlyDigits.length === 8 && lastFetchedCepRef.current !== onlyDigits) {
      debounceRef.current = setTimeout(() => {
        fetchCep(onlyDigits);
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.cep]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;

    setForm((prev) => ({ ...prev, [field]: value }));

    if (field === "email") {
      setEmailCheck({
        loading: false,
        exists: false,
        checkedEmail: "",
        helperText: "",
      });
    }
  };

  const verificarEmail = async (emailValue) => {
    const emailNormalizado = String(emailValue || "").trim().toLowerCase();

    if (!emailNormalizado) {
      setEmailCheck({
        loading: false,
        exists: false,
        checkedEmail: "",
        helperText: "",
      });
      return false;
    }

    setEmailCheck({
      loading: true,
      exists: false,
      checkedEmail: emailNormalizado,
      helperText: "",
    });

    try {
      const res = await api.post(`/teacher/email/verify`, {
        email: emailNormalizado,
      });

      const exists = !!res.data?.exists;

      setEmailCheck({
        loading: false,
        exists,
        checkedEmail: emailNormalizado,
        helperText: exists
          ? "Já existe uma conta com este e-mail. Faça login para continuar."
          : "",
      });

      return exists;
    } catch {
      setEmailCheck({
        loading: false,
        exists: false,
        checkedEmail: emailNormalizado,
        helperText: "",
      });

      return false;
    }
  };

  const escolherPlano = (tipoPlano) => {
    setPlanoSelecionado(tipoPlano);
    setPaymentMethod("CARD");
    setTab(0);
    // mantém UX limpa (se estava vendo cards)
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const voltarParaPlanos = () => {
    setPlanoSelecionado(null);
    setPaymentMethod("CARD");
    setTab(0);
    // limpa cartão (opcional)
    setCardForm({ number: "", name: "", expiry: "", cvv: "" });
  };

  // ===== validações por tab =====
  const validarTab0 = () => {
    if (!planoSelecionado) {
      showSnackbar("Selecione um plano para continuar.", "warning");
      return false;
    }
    if (
      !form.nome ||
      !form.email ||
      !form.senha ||
      !form.confirmar_senha ||
      !form.cpf_cnpj ||
      !form.celular
    ) {
      showSnackbar(
        "Preencha os campos obrigatórios dos dados pessoais.",
        "warning",
      );
      return false;
    }

    if (emailCheck.exists) {
      showSnackbar("Já existe uma conta com este e-mail. Faça login para continuar.", "warning");
      return false;
    }

    if (!form.celular) {
      showSnackbar("Preencha o celular.", "warning");
      return false;
    }
    if (!isNomeValido(form.nome)) {
      showSnackbar("Nome deve conter apenas letras.", "warning");
      return false;
    }

    if ((form.cpf_cnpj || "").length !== 14) {
      showSnackbar(
        "CPF incompleto. Preencha no formato 000.000.000-00.",
        "warning",
      );
      return false;
    }

    if ((form.celular || "").length !== 15) {
      showSnackbar(
        "Celular incompleto. Preencha no formato (00) 00000-0000.",
        "warning",
      );
      return false;
    }

    if (!isPasswordValid) {
      showSnackbar("A senha deve ter no mínimo 6 caracteres, contendo letras e números.", "warning");
      return false;
    }

    if (form.confirmar_senha.trim() !== passwordValue) {
      showSnackbar("Senhas não conferem.", "error");
      return false;
    }

    if (!termoAceito) {
      showSnackbar("É obrigatório aceitar o termo de uso.", "warning");
      return;
    }

    return true;
  };

  const validarTab1 = () => {
    if (
      !form.cep ||
      !form.endereco ||
      !form.numero ||
      !form.bairro ||
      !form.cidade ||
      !form.uf
    ) {
      showSnackbar("Preencha os campos obrigatórios do endereço.", "warning");
      return false;
    }
    return true;
  };

  const validarTab2 = () => {
    const num = digits(cardForm.number);
    const exp = digits(cardForm.expiry);
    const cvv = digits(cardForm.cvv);

    if (!cardForm.name || !num || !exp || !cvv) {
      showSnackbar("Preencha todos os dados do cartão.", "warning");
      return false;
    }

    if (cardForm.name.trim().length < 3)
      return (showSnackbar("Nome do titular inválido.", "warning"), false);
    if (exp.length !== 4)
      return (showSnackbar("Validade inválida.", "warning"), false);

    const b = detectBrand(num);
    const rule = CARD_RULES[b] || CARD_RULES.default;

    if (num.length !== rule.max) {
      return (showSnackbar("Número do cartão inválido.", "warning"), false);
    }

    // CVV: Amex = 4, demais = 3
    const cvvNeeded = b === "amex" ? 4 : 3;
    if (cvv.length !== cvvNeeded) {
      return (
        showSnackbar(`CVV inválido. Use ${cvvNeeded} dígitos.`, "warning"),
        false
      );
    }

    return true;
  };

  const autenticarAposCadastro = async (emailNormalizado) => {
    const loginRes = await api.post("/teacher/authenticate", {
      email: emailNormalizado,
      password: passwordValue,
    });

    const token = loginRes.data?.access_token || loginRes.data?.token;

    if (!token) return null;

    try {
      const profile = await api.get("/teacher", {
        headers: { Authorization: `Bearer ${token}` },
      });
      login(normalizeTeacher(profile.data), token);
    } catch (_) {
      login({ name: form.nome.trim(), email: emailNormalizado }, token);
    }

    return token;
  };

  const buildTeacherPayload = (emailNormalizado) => ({
    name: form.nome.trim(),
    email: emailNormalizado,
    cpf: form.cpf_cnpj,
    password: passwordValue,
    zipCode: form.cep || null,
    street: form.endereco || null,
    number: form.numero || null,
    complement: form.complemento || null,
    district: form.bairro || null,
    city: form.cidade || null,
    state: form.uf || null,
    phone: form.celular,
    referralCode: form.referralCode?.trim() || null,
  });

  const handleCriarAssinaturaPix = async () => {
    const emailNormalizado = String(form.email || "").trim().toLowerCase();
    if (emailNormalizado) {
      const exists = await verificarEmail(emailNormalizado);
      if (exists) {
        setTab(0);
        setLoadingCheckout(false);
        return;
      }
    }

    if (!validarTab0()) return setTab(0);
    if (!validarTab1()) return setTab(1);

    setLoadingCheckout(true);

    try {
      await api.post("/teacher", buildTeacherPayload(emailNormalizado));

      const token = await autenticarAposCadastro(emailNormalizado);

      if (!token) {
        showSnackbar("Conta criada, mas não foi possível autenticar automaticamente. Faça login.", "warning");
        navigate("/login");
        return;
      }

      const pixRes = await api.post("/billing/subscription/pix", {
        planName: planoSelecionado,
      });

      setPixData(normalizePixDialogData(pixRes.data, planoSelecionado));
      setPixOpen(true);
      showSnackbar("Conta criada! Agora pague o PIX para liberar o plano.", "info");
    } catch (err) {
      const message = getApiErrorMessage(err, "Não foi possível gerar sua assinatura PIX. Tente novamente.");

      if (err?.response?.status === 409 || message.toLowerCase().includes("e-mail")) {
        setTab(0);
        setEmailCheck({
          loading: false,
          exists: true,
          checkedEmail: emailNormalizado,
          helperText: "Já existe uma conta com este e-mail. Faça login para continuar.",
        });
      }

      showSnackbar(message, err?.response?.status === 409 ? "warning" : "error");
    } finally {
      setLoadingCheckout(false);
    }
  };

  const handleCriarAssinatura = async () => {

    const emailNormalizado = String(form.email || "").trim().toLowerCase();
    if (emailNormalizado) {
      const exists = await verificarEmail(emailNormalizado);
      if (exists) {
        setTab(0);
        setLoadingCheckout(false);
        return;
      }
    }

    if (!validarTab0()) return setTab(0);
    if (!validarTab1()) return setTab(1);
    if (paymentMethod === "CARD" && !validarTab2()) return setTab(2);

    const exp = digits(cardForm.expiry);
    const expiryMonth = exp.slice(0, 2);
    const expiryYear = `20${exp.slice(2)}`; // MM/AA -> 20AA

    setLoadingCheckout(true);
    try {
      await api.post("/teacher/register/credit-card", {
        name: form.nome.trim(),
        email: emailNormalizado,
        cpf: form.cpf_cnpj,
        password: passwordValue,
        zipCode: form.cep || null,
        street: form.endereco || null,
        number: form.numero || null,
        complement: form.complemento || null,
        district: form.bairro || null,
        city: form.cidade || null,
        state: form.uf || null,
        phone: form.celular,
        referralCode: form.referralCode?.trim() || null,
        planName: planoSelecionado,
        creditCard: {
          holderName: cardForm.name.trim(),
          number: digits(cardForm.number),
          expiryMonth,
          expiryYear,
          ccv: digits(cardForm.cvv).slice(0, 4),
        },
        creditCardHolderInfo: {
          name: form.nome.trim(),
          email: emailNormalizado,
          cpf: digits(form.cpf_cnpj),
          postalCode: digits(form.cep),
          addressNumber: form.numero || undefined,
          addressComplement: form.complemento || undefined,
          phone: digits(form.celular),
          mobilePhone: digits(form.celular),
        },
        trialDays: 7,
      });

      const loginRes = await api.post("/teacher/authenticate", {
        email: emailNormalizado,
        password: passwordValue,
      });

      const token = loginRes.data?.access_token || loginRes.data?.token;

      if (token) {
        try {
          const profile = await api.get("/teacher", {
            headers: { Authorization: `Bearer ${token}` },
          });
          login(normalizeTeacher(profile.data), token);
        } catch (_) {
          login({ name: form.nome.trim(), email: emailNormalizado }, token);
        }

        showSnackbar("Assinatura criada com sucesso! Bem-vindo(a).", "success");
        navigate("/planejamento");
        return;
      }

      showSnackbar(
        "Assinatura criada, mas não foi possível autenticar automaticamente. Faça login.",
        "warning",
      );
      navigate("/login");
    } catch (err) {
      const message = getApiErrorMessage(err, "Não foi possível concluir a assinatura. Tente novamente.");

      if (err?.response?.status === 409 || message.toLowerCase().includes("e-mail")) {
        setTab(0);
        setEmailCheck({
          loading: false,
          exists: true,
          checkedEmail: emailNormalizado,
          helperText: "Já existe uma conta com este e-mail. Faça login para continuar.",
        });
      }

      showSnackbar(message, err?.response?.status === 409 ? "warning" : "error");
    } finally {
      setLoadingCheckout(false);
    }
  };

  const onChangeTab = (_, newValue) => {
    // trava navegação “pra frente” sem validar
    if (newValue === 1 && !validarTab0()) return;
    if (newValue === 2 && (!validarTab0() || !validarTab1())) return;
    setTab(newValue);
  };

  // ===== UI: render de features IGUAL Header =====
  const renderFeatures = (plan) => {
    const isProfessor = plan.tipo === "PROFESSOR";
    const isMentor = plan.tipo === "MENTOR";
    const WHATSAPP = "Suporte por WhatsApp";

    const extrasRiscados = isProfessor
      ? PROFESSOR_EXTRAS_RISCADOS
      : isMentor
        ? MENTOR_EXTRAS_RISCADOS
        : [];
    const baseSemWhatsapp = plan.features.filter((f) => f !== WHATSAPP);
    const whatsappNoFinal = plan.features.includes(WHATSAPP) ? [WHATSAPP] : [];

    const featuresToRender =
      isProfessor || isMentor
        ? [...baseSemWhatsapp, ...extrasRiscados, ...whatsappNoFinal]
        : plan.features;

    return (
      <Stack spacing={1} sx={{ mb: 2, flexGrow: 1 }}>
        {featuresToRender.map((feature, i) => {
          const isRiscada =
            (isProfessor || isMentor) && extrasRiscados.includes(feature);
          const match = feature.match(/^(\d+)(.*)$/);

          return (
            <Box
              key={`${feature}-${i}`}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <CheckCircleRoundedIcon
                fontSize="small"
                sx={{ color: isRiscada ? "text.disabled" : "primary.main" }}
              />
              <Typography
                fontSize={15}
                sx={{
                  width: "100%",
                  textAlign: "left",
                  color: isRiscada ? "text.disabled" : "text.secondary",
                  textDecoration: isRiscada ? "line-through" : "none",
                }}
              >
                {match ? (
                  <>
                    <Box
                      component="span"
                      sx={{ fontWeight: 800, textDecoration: "underline" }}
                    >
                      {match[1]}
                    </Box>
                    {match[2]}
                  </>
                ) : (
                  feature
                )}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    );
  };

  const PLAN_STYLES = {
    PROFESSOR: { bg: (t) => t.palette.info.light, fg: (t) => t.palette.info.contrastText },
    MENTOR: { bg: () => "#20B2AA", fg: () => "#FFFFFF" },
    MESTRE: { bg: (t) => t.palette.secondary.light, fg: (t) => t.palette.secondary.contrastText },
  };

  const planosRender = useMemo(() => {
    return PLANS.map((plan) => {
      const valorOriginal = parseBRLToNumber(plan.price);
      const valorComDesconto =
        cupomInfo.valid && cupomInfo.descontoPercentual > 0
          ? calcDiscountedPrice(valorOriginal, cupomInfo.descontoPercentual)
          : valorOriginal;

      return {
        ...plan,
        valorOriginal,
        valorComDesconto,
        hasDiscount: cupomInfo.valid && cupomInfo.descontoPercentual > 0,
        descontoPercentual: cupomInfo.descontoPercentual,
      };
    });
  }, [cupomInfo]);

  const tabsSxMobile = {
    width: "100%",
    maxWidth: "100%",
    overflowX: "auto",
    "& .MuiTabs-scroller": { overflowX: "auto !important" },
    "& .MuiTabs-flexContainer": { width: "max-content" },
  };

  const tabsSxDesktop = {
    width: !isMobile ? "70%" : "100%",
    marginLeft: "auto",
    marginRight: "auto",
  };

  const CheckoutTrustBar = () => (
    <Paper
      elevation={0}
      sx={(theme) => ({
        width: !planoSelecionado && !isMobile ? "70%" : "100%",
        mt: 1.2,
        mb: 1.6,
        p: 1.6,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
        bgcolor:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.common.white, 0.04)
            : alpha(theme.palette.primary.main, 0.05),
      })}
    >
      <Grid container spacing={!isMobile ? 1.2 : 2} sx={{ display: "flex", flexDirection: !isMobile ? "row" : "column", alignItems: !isMobile ? "start" : "center" }}>
        {/* 7 dias */}
        <Grid item xs={12} md={!isMobile ? 6 : 12}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.1 }}>
            <Box
              sx={(theme) => ({
                width: 34,
                height: 34,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(theme.palette.success.main, 0.18),
                border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
                flexShrink: 0,
              })}
            >
              <VerifiedRoundedIcon
                sx={{ fontSize: 20, color: "success.main" }}
              />
            </Box>

            <Box sx={{ minWidth: 0, textAlign: "start" }}>

              {!isMobile ?

                (

                  <Typography sx={{ fontWeight: 1000, lineHeight: 1.12 }}>
                    No cartão, ative{" "}
                    <Box component="span" sx={{ color: "success.main" }}>
                      7 dias gratuitos
                    </Box>
                  </Typography>

                ) :

                (

                  <Typography sx={{ fontWeight: 1000, lineHeight: 1.12 }}>
                    No cartão, ative{" "}
                    <Box component="span" sx={{ color: "success.main" }}>
                      7 dias gratuitos
                    </Box>
                  </Typography>

                )}

              <Typography
                sx={{ color: "text.secondary", fontSize: 13, mt: 0.3 }}
              >
                {!isMobile ? "Se não fizer sentido, cancele dentro do período." : "Se não gostar, cancele dentro do período."}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Prova social */}
        <Grid item xs={12} md={!isMobile ? 6 : 12}>
          <Box
            sx={(theme) => ({
              display: "flex",
              alignItems: "flex-start",
              justifyContent: { xs: "flex-start", md: "flex-end" },
              gap: 1.2,
              flexWrap: "wrap",
            })}
          >

            <Box
              sx={(theme) => ({
                width: 34,
                height: 34,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(theme.palette.primary.main, 0.18),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                flexShrink: 0,
              })}
            >
              <GroupsRoundedIcon
                sx={{ fontSize: 20, color: "primary.main" }}
              />
            </Box>

            <Box sx={{ minWidth: 0, textAlign: "start" }}>
              <Typography sx={{ fontWeight: 1000, lineHeight: 1.1 }}>
                Diversos{" "}
                <Box component="span" sx={{ color: "primary.main" }}>
                  professores{" "}
                </Box>
                já usam
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 0.6,
                  flexWrap: "wrap",
                }}
              >

                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.6,
                  }}
                >

                  <Typography
                    sx={{ fontSize: 12.5, color: "text.secondary" }}
                  >
                    Economize horas na sua rotina também
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        py: isMobile ? 0 : 5,
      }}
    >
      <Box
        sx={{
          maxWidth: !planoSelecionado
            ? !isMobile
              ? "80%"
              : "100%"
            : !isMobile
              ? "60%"
              : "100%",
          mx: "auto",
          px: isMobile ? 0 : 3,
        }}
      >
        <Paper
          elevation={2}
          sx={{
            borderRadius: !isMobile ? 4 : 0,
            p: isMobile ? 3 : 4,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stack spacing={1.4} alignItems="center" sx={{ textAlign: "center" }}>
            <Typography variant={isMobile ? "h5" : "h4"} fontWeight={900}>
              {!planoSelecionado ? "Selecionar Plano" : "Confirmar Plano"}
            </Typography>

            <CheckoutTrustBar />

            {/* ====== MODO 1: sem plano selecionado (MOSTRA SÓ OS PLANOS) ====== */}
            {!planoSelecionado && (
              <>
                <Typography sx={{ color: "text.secondary", maxWidth: 680 }}>
                  Escolha o plano ideal para sua rotina. A forma de pagamento será escolhida na última etapa do cadastro.
                </Typography>

                {cupomInfo.loading && (
                  <Chip
                    label="Validando cupom..."
                    color="default"
                    sx={{ mt: 1, fontWeight: 700 }}
                  />
                )}

                {!cupomInfo.loading && cupomInfo.valid && (
                  <Chip
                    label={`Cupom aplicado • ${cupomInfo.descontoPercentual}% OFF na 1ª mensalidade`}
                    color="success"
                    sx={{ mt: 1, fontWeight: 800 }}
                  />
                )}

                <Box
                  sx={{
                    width: "100%",
                    mt: 2,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: 1100,
                      mx: "auto",
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))",
                      gap: isMobile ? 1.5 : 3.3,
                      alignItems: "stretch",
                    }}
                  >
                    {planosRender.map((plan) => {
                      const isHighlighted = plan.highlight;

                      return (
                        <Box
                          key={plan.tipo}
                          sx={{ display: "flex", mt: 1, minWidth: 0 }}
                        >
                          <Paper
                            elevation={isHighlighted ? 6 : 2}
                            sx={(theme) => {
                              const lilac = "#7f72f7";
                              const isDark = theme.palette.mode === "dark";
                              return {
                                p: 4,
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                position: "relative",
                                transform:
                                  isHighlighted && !isMobile
                                    ? "scale(1.02)"
                                    : "none",
                                transition: "transform 160ms ease",
                                borderRadius: 4,
                                border: isHighlighted
                                  ? `1.5px solid ${isDark ? alpha(lilac, 0.6) : lilac}`
                                  : `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                                background: isHighlighted
                                  ? isDark
                                    ? `linear-gradient(180deg, ${alpha(lilac, 0.16)} 0%, ${alpha(
                                      theme.palette.background.paper,
                                      0.8,
                                    )} 100%)`
                                    : "linear-gradient(180deg, #ede9fe 50%, #f3f4f6 100%)"
                                  : undefined,
                                boxShadow: isHighlighted
                                  ? isDark
                                    ? `0 4px 16px ${alpha(lilac, 0.25)}`
                                    : "0 4px 16px #bcb7fd44"
                                  : undefined,
                              };
                            }}
                          >
                            {isHighlighted && (
                              <Chip
                                label="Mais escolhido"
                                color="secondary"
                                size="small"
                                sx={{
                                  position: "absolute",
                                  top: 6,
                                  left: "50%",
                                  transform: "translateX(-50%)",
                                  fontWeight: 700,
                                }}
                              />
                            )}

                            <Typography
                              variant="h6"
                              fontWeight={700}
                              color={isHighlighted ? "primary" : "text.primary"}
                              sx={{ textAlign: "center" }}
                            >
                              {plan.title}
                            </Typography>

                            <Box sx={{ textAlign: "center", mt: 1 }}>
                              <Typography
                                sx={(theme) => ({
                                  fontSize: 14,
                                  fontWeight: 800,
                                  textDecoration: "line-through",
                                  color: alpha(
                                    theme.palette.text.primary,
                                    theme.palette.mode === "dark" ? 0.55 : 0.5,
                                  ),
                                  lineHeight: 1.1,
                                })}
                              >
                                {!plan.hasDiscount ? plan.priceDisabled : ``}
                              </Typography>

                              <Typography
                                variant="h5"
                                fontWeight={900}
                                sx={{
                                  mt: 0.3,
                                  color:
                                    theme.palette.mode === "light"
                                      ? "#444"
                                      : "#f3f4f6",
                                  lineHeight: 1.1,
                                }}
                              >
                                {plan.hasDiscount
                                  ? `${formatBRL(plan.valorComDesconto)}/mês`
                                  : `${plan.price}`}
                              </Typography>

                              {plan.hasDiscount && (
                                <>
                                  <Typography
                                    sx={{
                                      mt: 0.45,
                                      fontSize: 13,
                                      color: "text.secondary",
                                      textDecoration: "line-through",
                                    }}
                                  >
                                    1ª mensalidade sem cupom: {formatBRL(plan.valorOriginal)}
                                  </Typography>

                                  <Chip
                                    label="Cupom aplicado"
                                    color="success"
                                    size="small"
                                    sx={{ mt: 1, fontWeight: 700 }}
                                  />
                                </>
                              )}
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            {renderFeatures(plan)}

                            <Stack spacing={1.1} sx={{ mt: "auto" }}>
                              <Button
                                variant="contained"
                                size="large"
                                sx={{
                                  width: "100%",
                                  fontWeight: 900,
                                  fontSize: 15,
                                  textTransform: "none",
                                  py: 1.25,
                                  borderRadius: 3,
                                  boxShadow: "0 2px 6px #8f8ef366",
                                  bgcolor: isHighlighted
                                    ? CTA_GREEN_HIGHLIGHT
                                    : CTA_GREEN_PLANOS,
                                  "&:hover": {
                                    bgcolor: isHighlighted
                                      ? CTA_GREEN_HIGHLIGHT_HOVER
                                      : CTA_GREEN_PLANOS_HOVER,
                                  },
                                }}
                                onClick={() => escolherPlano(plan.tipo)}
                              >
                                Selecionar plano
                              </Button>

                              <Typography variant="caption" sx={{ color: "text.secondary", textAlign: "center", lineHeight: 1.25 }}>
                                Cartão com 7 dias grátis ou PIX mensal na última etapa.
                              </Typography>
                            </Stack>
                          </Paper>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </>
            )}

            {/* ====== MODO 2: com plano selecionado (MOSTRA SÓ TABS + FORM) ====== */}
            {planoSelecionado && (
              <>
                <Box
                  sx={{
                    mt: 1.2,
                    display: "flex",
                    gap: 1,
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  <Chip
                    label={PLAN_LABEL[planoSelecionado] || planoSelecionado}
                    sx={(theme) => ({
                      fontWeight: 800,
                      bgcolor: PLAN_STYLES[planoSelecionado]?.bg(theme) || "action.selected",
                      color: PLAN_STYLES[planoSelecionado]?.fg(theme) || theme.palette.text.primary,
                    })}
                  />
                  <Button
                    variant="text"
                    onClick={voltarParaPlanos}
                    sx={{
                      textTransform: "none",
                      textDecoration: "underline",
                      "&:hover": {
                        textDecoration: "underline",
                        backgroundColor: "transparent",
                        opacity: 0.8,
                      },
                    }}
                  >
                    Trocar plano
                  </Button>
                </Box>

                <Divider sx={{ my: 2.5, width: "100%" }} />

                <Box sx={{ width: "100%" }}>
                  <Tabs
                    value={tab}
                    onChange={onChangeTab}
                    variant={isMobile ? "scrollable" : "fullWidth"}
                    scrollButtons={isMobile ? "auto" : false}
                    allowScrollButtonsMobile={isMobile}
                    sx={{
                      ...(isMobile ? tabsSxMobile : tabsSxDesktop),
                    }}
                  >
                    <Tab label="Dados Pessoais" />
                    <Tab label="Endereço" />
                    <Tab label="Forma de Pagamento" />
                  </Tabs>

                  <Box sx={{ mt: 4 }}>
                    {/* TAB 0 */}
                    {tab === 0 && (
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Nome"
                            fullWidth
                            value={form.nome}
                            onChange={(e) =>
                              setForm((prev) => ({
                                ...prev,
                                nome: onlyLettersSpaces(e.target.value),
                              }))
                            }
                            onBlur={() =>
                              setForm((prev) => ({
                                ...prev,
                                nome: toTitleCaseNome(prev.nome),
                              }))
                            }
                            required
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            label="CPF"
                            fullWidth
                            value={form.cpf_cnpj}
                            onChange={(e) =>
                              setForm((prev) => ({
                                ...prev,
                                cpf_cnpj: mascaraCpfCnpj(e.target.value),
                              }))
                            }
                            placeholder="000.000.000-00"
                            inputMode="numeric"
                            inputProps={{ maxLength: 14 }}
                            required
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Celular"
                            fullWidth
                            value={form.celular}
                            onChange={(e) =>
                              setForm((prev) => ({
                                ...prev,
                                celular: mascaraCelular(e.target.value),
                              }))
                            }
                            placeholder="(00) 00000-0000"
                            inputMode="tel"
                            inputProps={{ maxLength: 15 }}
                            required
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            label="E-mail"
                            fullWidth
                            value={form.email}
                            onChange={handleChange("email")}
                            onBlur={async () => {
                              const emailNormalizado = String(form.email || "").trim().toLowerCase();
                              if (!emailNormalizado) return;
                              await verificarEmail(emailNormalizado);
                            }}
                            inputMode="email"
                            placeholder="E-mail para acesso à plataforma"
                            required
                            error={emailCheck.exists}
                            helperText={emailCheck.helperText}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  {emailCheck.loading ? <CircularProgress size={18} /> : null}
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Senha"
                            fullWidth
                            type="password"
                            placeholder="Senha para acesso à plataforma"
                            value={form.senha}
                            onChange={handleChange("senha")}
                            required
                            error={form.senha.length > 0 && !isPasswordValid}
                            helperText={passwordHelperText}
                            FormHelperTextProps={{
                              sx: {
                                color: form.senha.length > 0 && isPasswordValid ? "success.main" : undefined,
                                fontWeight: form.senha.length > 0 ? 600 : 400,
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Confirmar Senha"
                            fullWidth
                            type="password"
                            placeholder="Repita a senha"
                            value={form.confirmar_senha}
                            onChange={handleChange("confirmar_senha")}
                            required
                            error={form.confirmar_senha.length > 0 && form.confirmar_senha.trim() !== passwordValue}
                            helperText={
                              form.confirmar_senha.length > 0 && form.confirmar_senha.trim() !== passwordValue
                                ? "As senhas não conferem."
                                : " "
                            }
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            label="Código Indicação (opcional)"
                            fullWidth
                            value={form.referralCode}
                            onChange={handleChange("referralCode")}
                            disabled={indicacaoBloqueada}
                            helperText={
                              indicacaoBloqueada
                                ? "Código aplicado automaticamente a partir do link de indicação."
                                : "Se você recebeu um código de um influenciador, informe aqui."
                            }
                            InputProps={{
                              endAdornment: !isMobile ? (
                                <InputAdornment position="end">
                                  {cupomInfo.loading ? (
                                    <CircularProgress size={18} />
                                  ) : cupomInfo.valid ? (
                                    <Chip
                                      size="small"
                                      color="success"
                                      icon={<DiscountIcon fontSize="small" sx={{ verticalAlign: "middle" }} />}
                                      label={`Cupom aplicado • ${cupomInfo.descontoPercentual}% OFF`}
                                      sx={{
                                        fontWeight: 800,
                                        maxWidth: 230,
                                        "& .MuiChip-label": {
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        },
                                      }}
                                    />
                                  ) : null}
                                </InputAdornment>
                              ) : null,
                            }}
                            onBlur={async () => {
                              const ref = form.referralCode?.trim();

                              if (!ref) {
                                setCupomInfo({
                                  loading: false,
                                  valid: false,
                                  descontoPercentual: 0,
                                  indicacaoRef: "",
                                });
                                return;
                              }

                              setCupomInfo({
                                loading: true,
                                valid: false,
                                descontoPercentual: 0,
                                indicacaoRef: ref,
                              });

                              try {
                                const res = await api.get(`/partner/cupom/${encodeURIComponent(ref)}`);

                                setCupomInfo({
                                  loading: false,
                                  valid: !!res.data?.valid,
                                  descontoPercentual: Number(res.data?.couponDiscountPercentage || 0),
                                  indicacaoRef: ref,
                                });
                              } catch {
                                setCupomInfo({
                                  loading: false,
                                  valid: false,
                                  descontoPercentual: 0,
                                  indicacaoRef: ref,
                                });
                              }
                            }}
                          />

                          {isMobile && cupomInfo.loading && (
                            <Box sx={{ mt: 1 }}>
                              <CircularProgress size={18} />
                            </Box>
                          )}

                          {isMobile && cupomInfo.valid && !cupomInfo.loading && (
                            <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-start" }}>
                              <Chip
                                size="small"
                                color="success"
                                icon={<DiscountIcon fontSize="small" sx={{ verticalAlign: "middle" }} />}
                                label={`Cupom aplicado • ${cupomInfo.descontoPercentual}% OFF`}
                                sx={{
                                  fontWeight: 800,
                                  maxWidth: "100%",
                                  "& .MuiChip-label": {
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  },
                                }}
                              />
                            </Box>
                          )}
                        </Grid>

                        <FormControlLabel
                          sx={{ alignItems: "center", mt: 1.5, mb: 0.5, pl: 2.7 }}
                          control={
                            <Checkbox
                              checked={termoAceito}
                              onChange={(e) => setTermoAceito(e.target.checked)}
                              disabled={loadingCheckout}
                              color="primary"
                              sx={{ p: 0.5, mt: 0.2 }}
                            />
                          }
                          label={
                            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
                              <Typography variant="body2" component="span">
                                Declaro que li e aceito o{" "}
                                <span
                                  style={{
                                    color: "#1976d2",
                                    cursor: "pointer",
                                    fontWeight: 600,
                                    textDecoration: "underline",
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setOpenTermoModal(true);
                                  }}
                                >
                                  termo de uso
                                </span>
                                .
                              </Typography>
                              <Typography variant="body2" component="span" sx={{ ml: 0.5, fontWeight: 700 }}>
                                *
                              </Typography>
                            </Box>
                          }
                        />

                        <Grid item xs={12}>
                          <Divider variant="middle" sx={{ my: 2 }} />
                        </Grid>

                        <Grid item xs={12}>
                          <Button
                            variant="contained"
                            onClick={() => {
                              if (validarTab0()) setTab(1);
                            }}
                            sx={{
                              textTransform: "none",
                              fontWeight: 800,
                              borderRadius: 3,
                              py: 1.2,
                              bgcolor: CTA_GREEN_PLANOS,
                              "&:hover": {
                                bgcolor: CTA_GREEN_PLANOS_HOVER,
                              },
                            }}
                            fullWidth
                          >
                            Continuar
                          </Button>
                        </Grid>
                      </Grid>
                    )}

                    {/* TAB 1 */}
                    {tab === 1 && (
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <TextField
                            label="CEP"
                            fullWidth
                            value={form.cep}
                            onChange={(e) =>
                              setForm((prev) => ({
                                ...prev,
                                cep: mascaraCEP(e.target.value),
                              }))
                            }
                            placeholder="00000-000"
                            inputMode="numeric"
                            helperText={cepHelper}
                            FormHelperTextProps={{
                              sx: {
                                color:
                                  cepStatus === "error"
                                    ? "error.main"
                                    : "text.secondary",
                              },
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  {cepStatus === "loading" ? (
                                    <CircularProgress size={18} />
                                  ) : cepStatus === "ok" ? (
                                    <Chip
                                      size="small"
                                      color="success"
                                      label="OK"
                                      sx={{ fontWeight: 800 }}
                                    />
                                  ) : null}
                                </InputAdornment>
                              ),
                            }}
                            required
                          />
                        </Grid>

                        <Grid item xs={12} md={8}>
                          <TextField
                            label="Endereço"
                            fullWidth
                            value={form.endereco}
                            onChange={handleChange("endereco")}
                            required
                          />
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <TextField
                            id="Numero"
                            label="Número"
                            fullWidth
                            value={form.numero}
                            onChange={handleChange("numero")}
                            inputMode="numeric"
                            required
                          />
                        </Grid>

                        <Grid item xs={12} md={8}>
                          <TextField
                            label="Complemento (opcional)"
                            fullWidth
                            value={form.complemento}
                            onChange={handleChange("complemento")}
                          />
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <TextField
                            label="Bairro"
                            fullWidth
                            value={form.bairro}
                            onChange={handleChange("bairro")}
                            required
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Cidade"
                            fullWidth
                            value={form.cidade}
                            onChange={handleChange("cidade")}
                            required
                          />
                        </Grid>

                        <Grid item xs={12} md={2}>
                          <TextField
                            label="UF"
                            fullWidth
                            value={form.uf}
                            onChange={handleChange("uf")}
                            inputProps={{ maxLength: 2 }}
                            required
                          />
                        </Grid>


                        <Grid item xs={12}>
                          <Divider variant="middle" sx={{ my: 2 }} />
                        </Grid>

                        <Grid item xs={12} sx={{ display: "flex", gap: 1 }}>
                          <Button
                            variant="outlined"
                            onClick={() => setTab(0)}
                            sx={{
                              textTransform: "none",
                              fontWeight: 800,
                              borderRadius: 3,
                              py: 1.2,

                              // cores do outlined
                              color: CTA_GREEN_PLANOS,
                              borderColor: CTA_GREEN_PLANOS,
                              "&:hover": {
                                borderColor: CTA_GREEN_PLANOS_HOVER,
                                color: CTA_GREEN_PLANOS_HOVER,

                                // se quiser um "hover preenchido" levinho:
                                bgcolor: "rgba(0,0,0,0.04)", // ou use alpha(CTA_GREEN_PLANOS, 0.08)
                              },
                            }}
                            fullWidth
                          >
                            Voltar
                          </Button>


                          <Button
                            variant="contained"
                            onClick={() => {
                              if (!validarTab1()) return;
                              setTab(2);
                            }}
                            sx={{
                              textTransform: "none",
                              fontWeight: 800,
                              borderRadius: 3,
                              py: 1.2,
                              bgcolor: CTA_GREEN_PLANOS,
                              "&:hover": {
                                bgcolor: CTA_GREEN_PLANOS_HOVER,
                              },
                            }}
                            fullWidth
                            disabled={loadingCheckout}
                          >
                            Continuar
                          </Button>
                        </Grid>
                      </Grid>
                    )}

                    {/* TAB 2 */}
                    {tab === 2 && (
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography sx={{ fontWeight: 900, textAlign: "left", mb: 1 }}>
                            Escolha a forma de pagamento
                          </Typography>

                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Paper
                                elevation={0}
                                onClick={() => setPaymentMethod("CARD")}
                                sx={(theme) => ({
                                  p: 2,
                                  height: "100%",
                                  cursor: "pointer",
                                  borderRadius: 3,
                                  textAlign: "left",
                                  border: `2px solid ${paymentMethod === "CARD" ? CTA_GREEN_PLANOS : alpha(theme.palette.divider, 0.9)}`,
                                  bgcolor: paymentMethod === "CARD"
                                    ? alpha(theme.palette.success.main, theme.palette.mode === "dark" ? 0.12 : 0.07)
                                    : "background.paper",
                                  transition: "border-color 160ms ease, background-color 160ms ease",
                                  "&:hover": {
                                    borderColor: CTA_GREEN_PLANOS,
                                  },
                                })}
                              >
                                <Box sx={{ display: "flex", gap: 1.2, alignItems: "flex-start" }}>
                                  <CreditCardRoundedIcon sx={{ color: CTA_GREEN_PLANOS, mt: 0.2 }} />
                                  <Box>
                                    <Typography sx={{ fontWeight: 1000, lineHeight: 1.15 }}>
                                      Cartão de crédito
                                    </Typography>
                                    <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.5, lineHeight: 1.4 }}>
                                      Comece com 7 dias gratuitos. A primeira cobrança ocorre somente após o período de teste.
                                    </Typography>
                                  </Box>
                                </Box>
                              </Paper>
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <Paper
                                elevation={0}
                                onClick={() => setPaymentMethod("PIX")}
                                sx={(theme) => ({
                                  p: 2,
                                  height: "100%",
                                  cursor: "pointer",
                                  borderRadius: 3,
                                  textAlign: "left",
                                  border: `2px solid ${paymentMethod === "PIX" ? theme.palette.success.main : alpha(theme.palette.divider, 0.9)}`,
                                  bgcolor: paymentMethod === "PIX"
                                    ? alpha(theme.palette.success.main, theme.palette.mode === "dark" ? 0.12 : 0.07)
                                    : "background.paper",
                                  transition: "border-color 160ms ease, background-color 160ms ease",
                                  "&:hover": {
                                    borderColor: theme.palette.success.main,
                                  },
                                })}
                              >
                                <Box sx={{ display: "flex", gap: 1.2, alignItems: "flex-start" }}>
                                  <PixIcon sx={{ color: "success.main", mt: 0.2 }} />
                                  <Box>
                                    <Typography sx={{ fontWeight: 1000, lineHeight: 1.15 }}>
                                      PIX mensal
                                    </Typography>
                                    <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.5, lineHeight: 1.4 }}>
                                      Gera um QR Code PIX. O plano libera automaticamente após a confirmação do pagamento.
                                    </Typography>
                                  </Box>
                                </Box>
                              </Paper>
                            </Grid>
                          </Grid>
                        </Grid>

                        {paymentMethod === "CARD" && (
                          <>
                            <Grid item xs={12}>
                              <Divider variant="middle" sx={{ my: 1 }} />
                            </Grid>

                            <Grid item xs={12}>
                              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                                {["visa", "mastercard", "elo", "diners", "discover", "amex", "hipercard", "jcb"].map((b) => (
                                  <Box
                                    key={b}
                                    component="img"
                                    alt={b}
                                    src={`/cards/${b}.svg`}
                                    sx={{
                                      height: 22,
                                      opacity: brand && brand !== b ? 0.35 : 1,
                                    }}
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                    }}
                                  />
                                ))}
                              </Box>

                              <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary", textAlign: "left" }}>
                                <InfoIcon sx={{ fontSize: 16, verticalAlign: "middle", mr: 0.5 }} />
                                Seus dados de cartão são enviados com segurança para o provedor de pagamento (tokenização).
                              </Typography>
                            </Grid>

                            <Grid item xs={12}>
                              <TextField
                                label="Número do cartão"
                                value={cardForm.number}
                                onChange={(e) => setCardForm((v) => ({ ...v, number: formatCardNumber(e.target.value) }))}
                                fullWidth
                                inputMode="numeric"
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <Typography variant="caption" sx={{ fontWeight: 900 }}>
                                        {brand ? brand.toUpperCase() : ""}
                                      </Typography>
                                    </InputAdornment>
                                  ),
                                }}
                                required
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <TextField
                                label="Nome do titular"
                                value={cardForm.name}
                                onChange={(e) => setCardForm((v) => ({ ...v, name: onlyLetters(e.target.value) }))}
                                fullWidth
                                required
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField
                                label="Validade (MM/AA)"
                                value={cardForm.expiry}
                                onChange={(e) => setCardForm((v) => ({ ...v, expiry: formatExpiry(e.target.value) }))}
                                fullWidth
                                inputMode="numeric"
                                required
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField
                                label="CVV"
                                value={cardForm.cvv}
                                onChange={(e) => {
                                  const b = detectBrand(cardForm.number);
                                  const max = b === "amex" ? 4 : 3;
                                  setCardForm((v) => ({ ...v, cvv: digits(e.target.value).slice(0, max) }));
                                }}
                                fullWidth
                                inputMode="numeric"
                                required
                              />
                            </Grid>

                            <Grid item xs={12} md={7}>
                              <Paper
                                elevation={0}
                                sx={(theme) => ({
                                  mt: 1.2,
                                  p: 1.4,
                                  borderRadius: 3,
                                  border: `1px solid ${alpha(theme.palette.success.main, 0.28)}`,
                                  bgcolor: alpha(theme.palette.success.main, 0.08),
                                  textAlign: "left",
                                })}
                              >
                                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                                  <VerifiedRoundedIcon sx={{ color: "success.main", mt: 0.2 }} />
                                  <Box>
                                    <Typography sx={{ fontWeight: 1000, lineHeight: 1.1 }}>
                                      {!isMobile ? "Você está a um passo de ativar seus 7 dias grátis" : "Finalize e ative 7 dias grátis"}
                                    </Typography>
                                    <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.4 }}>
                                      <Box sx={{ display: "inline-flex", alignItems: "flex-start", gap: 0.3 }}>
                                        <LockRoundedIcon sx={{ fontSize: 17, color: "text.secondary" }} />
                                        {!isMobile ? "Assinatura rápida, checkout seguro e acesso imediato." : "Checkout seguro e acesso imediato."}
                                      </Box>
                                    </Typography>
                                  </Box>
                                </Box>
                              </Paper>
                            </Grid>
                          </>
                        )}

                        {paymentMethod === "PIX" && (
                          <Grid item xs={12}>
                            <Paper
                              elevation={0}
                              sx={(theme) => ({
                                mt: 1.2,
                                p: 1.6,
                                borderRadius: 3,
                                border: `1px solid ${alpha(theme.palette.success.main, 0.28)}`,
                                bgcolor: alpha(theme.palette.success.main, theme.palette.mode === "dark" ? 0.1 : 0.06),
                                textAlign: "left",
                              })}
                            >
                              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                                <PixIcon sx={{ color: "success.main", mt: 0.15 }} />
                                <Box>
                                  <Typography sx={{ fontWeight: 1000, lineHeight: 1.15 }}>
                                    PIX mensal sem inserir cartão
                                  </Typography>
                                  <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.4, lineHeight: 1.4 }}>
                                    Após criar sua conta, vamos gerar um QR Code PIX. O plano fica pendente até a confirmação do pagamento e libera automaticamente em seguida.
                                  </Typography>
                                </Box>
                              </Box>
                            </Paper>
                          </Grid>
                        )}

                        <Grid item xs={12}>
                          <Divider variant="middle" sx={{ my: 2 }} />
                        </Grid>

                        <Grid item xs={12} sx={{ display: "flex", gap: 1 }}>
                          <Button
                            variant="outlined"
                            onClick={() => setTab(1)}
                            sx={{
                              textTransform: "none",
                              fontWeight: 800,
                              borderRadius: 3,
                              py: 1.2,
                              color: CTA_GREEN_PLANOS,
                              borderColor: CTA_GREEN_PLANOS,
                              "&:hover": {
                                borderColor: CTA_GREEN_PLANOS_HOVER,
                                color: CTA_GREEN_PLANOS_HOVER,
                                bgcolor: "rgba(0,0,0,0.04)",
                              },
                            }}
                            fullWidth
                            disabled={loadingCheckout}
                          >
                            Voltar
                          </Button>

                          <Button
                            variant="contained"
                            onClick={paymentMethod === "PIX" ? handleCriarAssinaturaPix : handleCriarAssinatura}
                            sx={{
                              textTransform: "none",
                              fontWeight: 900,
                              borderRadius: 3,
                              py: 1.2,
                              bgcolor: paymentMethod === "PIX" ? "success.main" : "#2ECC71",
                              "&:hover": {
                                bgcolor: paymentMethod === "PIX" ? "success.dark" : "#27AE60",
                              },
                            }}
                            fullWidth
                            disabled={loadingCheckout}
                          >
                            {loadingCheckout ? (
                              <CircularProgress size={22} sx={{ color: "white" }} />
                            ) : paymentMethod === "PIX" ? (
                              "Gerar PIX mensal"
                            ) : !isMobile ? (
                              "Confirmar assinatura"
                            ) : (
                              "Confirmar"
                            )}
                          </Button>
                        </Grid>
                      </Grid>
                    )}
                  </Box>
                </Box>
              </>
            )}
          </Stack>

          {planoSelecionado && isMobile && (
            <>
              <Divider sx={{ my: 2.5 }} />

              <Typography align="center" variant="body2" mt={2} mb={!isMobile ? 0 : 2}>
                Já tem uma conta?{" "}
                <Link component="button" underline="hover" onClick={() => navigate("/login")}>
                  Entrar
                </Link>
              </Typography>
            </>
          )}

        </Paper>

        {planoSelecionado && !isMobile && (
          <Typography align="center" variant="body2" mt={2} mb={!isMobile ? 0 : 2}>
            Já tem uma conta?{" "}
            <Link component="button" underline="hover" onClick={() => navigate("/login")}>
              Entrar
            </Link>
          </Typography>
        )}

      </Box>

      <Dialog
        open={openTermoModal}
        onClose={() => setOpenTermoModal(false)}
        scroll="paper"
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{
          sx: { p: 0, borderRadius: isMobile ? 0 : 3 },
        }}
      >
        <DialogTitle>
          Termo de Uso
          <IconButton
            onClick={() => setOpenTermoModal(false)}
            sx={{ position: "absolute", right: 8, top: 10 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ overflowY: "auto" }}>
          <TermoUsoConteudo />
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setOpenTermoModal(false)}
            color="primary"
            variant="outlined"
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <PixSubscriptionDialog
        open={pixOpen}
        pixData={pixData}
        planName={pixData?.planName || planoSelecionado}
        amount={pixData?.amount || getPlanMonthlyValue(planoSelecionado)}
        title="PIX mensal da assinatura"
        subtitle="Pague a primeira mensalidade por PIX. Seu plano será liberado automaticamente assim que o pagamento for confirmado."
        closeLabel="Ir para a plataforma"
        onClose={() => {
          setPixOpen(false);
          navigate("/planejamento");
        }}
        onPaid={() => {
          setPixOpen(false);
          navigate("/planejamento");
        }}
      />

    </Box>
  );
}
